import { useState, type CSSProperties } from 'react'
import { formatShortDate, relativeDay } from '@app/shared'
import type { ISODate } from '@app/shared'

import {
  useToggleLearningItem,
  type LearningTrackDetail,
  type LearningUnit,
  type LearningUnitItem,
} from '../../lib/data'
import { ChevronDown, ExternalLink, Tick } from './icons'

// ============================================================================
// THE CURRICULUM OUTLINE — docs/design/handoff/"LifeOS Learning.dc.html", option 9b.
//
// Units → daily items, with a current-position marker and future units collapsed.
//
// COLLAPSED BY DEFAULT is the README's word (§8: "a curriculum outline with a current-position
// marker and future units collapsed") and the brief's. The rendered mockup pre-expands units 4
// and 5 to show what an open one looks like — click either and you are there. Everything before
// the current unit is collapsed too: a finished unit is a receipt, not a reading surface.
//
// THE CURRENT UNIT IS THE ONE HOLDING THE NEXT UNFINISHED ITEM, decided by the data contract. It
// gets the teal frame, the CURRENT pill, and its items open two rows either side of where you
// actually are — which is the whole reason the outline exists rather than a progress bar.
//
// A UNIT SHOWS A PREVIEW, then says how many it is holding back. "18 more items in this unit" is a
// count, and clicking it shows those exact eighteen. It never rounds and it never says "more".
// ============================================================================

/** Rows shown before the "N more items in this unit" disclosure opens the rest. */
const PREVIEW_CURRENT = 6
const PREVIEW_FUTURE = 3

export function Curriculum({ detail, today }: { detail: LearningTrackDetail; today: ISODate }) {
  const { units, track } = detail
  const currentNumber = units.find((u) => u.state === 'current')?.number
  const [open, setOpen] = useState<Record<number, boolean>>({})
  const [expanded, setExpanded] = useState<Record<number, boolean>>({})

  const isOpen = (unit: LearningUnit) => open[unit.number] ?? unit.number === currentNumber
  const anyOpen = units.some(isOpen)

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-[11px] overflow-hidden rounded-context border border-line bg-surface px-[18px] py-4 shadow-card">
      <header className="flex flex-none items-center gap-[10px]">
        <h2 className="text-[12.5px] font-semibold leading-none">Curriculum</h2>
        <span className="text-micro font-normal text-microcopy">
          {units.length} units · {track.itemsTotal} daily items
        </span>
        <button
          type="button"
          onClick={() =>
            setOpen(
              anyOpen
                ? Object.fromEntries(units.map((u) => [u.number, false]))
                : Object.fromEntries(units.map((u) => [u.number, true])),
            )
          }
          className="ml-auto text-micro font-medium text-muted transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:text-ink"
        >
          {anyOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-2 [margin-right:-8px]">
        {units.map((unit) => (
          <UnitBlock
            key={unit.id}
            unit={unit}
            today={today}
            open={isOpen(unit)}
            onToggleOpen={() => setOpen((s) => ({ ...s, [unit.number]: !isOpen(unit) }))}
            expanded={expanded[unit.number] ?? false}
            onExpand={() => setExpanded((s) => ({ ...s, [unit.number]: !s[unit.number] }))}
            nextItemId={detail.nextItem?.id ?? null}
          />
        ))}
      </div>
    </section>
  )
}

function UnitBlock({
  unit,
  today,
  open,
  onToggleOpen,
  expanded,
  onExpand,
  nextItemId,
}: {
  unit: LearningUnit
  today: ISODate
  open: boolean
  onToggleOpen: () => void
  expanded: boolean
  onExpand: () => void
  /** The one item in the whole outline that wears the `today` chip. */
  nextItemId: string | null
}) {
  const current = unit.state === 'current'
  const preview = current ? PREVIEW_CURRENT : PREVIEW_FUTURE
  const shown = expanded ? unit.items : unit.items.slice(0, preview)
  const hidden = unit.items.length - shown.length

  return (
    <div
      className={[
        'flex-none overflow-hidden rounded-row',
        current ? 'border-[1.5px] border-teal-200 bg-surface-teal' : 'border border-fill',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={onToggleOpen}
        aria-expanded={open}
        style={{ '--mo-rule': 'var(--color-learning)' } as CSSProperties}
        className={[
          'mo-rule flex w-full items-center gap-[10px] px-[13px] text-left',
          current
            ? 'bg-teal-fill py-[10px]'
            : unit.state === 'done'
              ? 'bg-surface-2 py-[9px]'
              : 'py-[9px]',
        ].join(' ')}
      >
        {current ? (
          <span
            className="size-[13px] flex-none rounded-full border-2 border-learning"
            aria-hidden
          />
        ) : unit.state === 'done' ? (
          <Tick size={13} className="flex-none text-success-dot" />
        ) : (
          <ChevronDown
            size={13}
            className={[
              'flex-none text-faintest-2 transition-transform duration-[var(--dur-fast)] ease-[var(--ease-out)]',
              open ? 'rotate-180' : '',
            ].join(' ')}
          />
        )}

        <span
          className={[
            'min-w-0 truncate text-[12px] leading-none',
            current
              ? 'font-semibold text-teal'
              : unit.state === 'done'
                ? 'font-semibold text-muted'
                : 'font-medium text-ink-2',
          ].join(' ')}
        >
          {unit.name}
        </span>

        {current && (
          <span className="flex-none rounded-pill bg-learning px-[7px] py-[2px] text-[9.5px] font-bold leading-[1.6] text-on-dark">
            CURRENT
          </span>
        )}

        <span
          className={[
            'ml-auto flex-none whitespace-nowrap text-[10.5px] font-normal leading-none',
            current ? 'text-teal' : 'text-microcopy',
          ].join(' ')}
        >
          {unit.state === 'done'
            ? // "done Aug 22" is the day the last item was ACTUALLY finished, not the day the plan
              // had it. A unit still open never gets a date it has not earned.
              `${unit.itemsDone} of ${unit.itemsTotal} · done ${unit.completedDate ? formatShortDate(unit.completedDate) : '—'}`
            : current
              ? `${unit.itemsDone} of ${unit.itemsTotal} · week ${unit.week}`
              : `${unit.itemsTotal} items · from ${formatShortDate(unit.startsOn)}`}
        </span>
      </button>

      {open && (
        <div className={['flex flex-col', current ? '' : 'border-t border-line-5'].join(' ')}>
          {shown.map((item) => (
            <ItemRow key={item.id} item={item} today={today} isNext={item.id === nextItemId} />
          ))}
          {hidden > 0 && (
            <button
              type="button"
              onClick={onExpand}
              className={[
                'flex items-center gap-[9px] px-[14px] pb-[11px] pt-2 text-left text-micro font-medium',
                current ? 'text-teal' : 'text-ink-3',
              ].join(' ')}
            >
              {hidden} more {hidden === 1 ? 'item' : 'items'} in this unit
              <ChevronDown size={12} className={current ? 'text-learning' : 'text-microcopy'} />
            </button>
          )}
          {expanded && unit.items.length > preview && (
            <button
              type="button"
              onClick={onExpand}
              className={[
                'flex items-center gap-[9px] px-[14px] pb-[11px] pt-2 text-left text-micro font-medium',
                current ? 'text-teal' : 'text-ink-3',
              ].join(' ')}
            >
              Show fewer
              <ChevronDown
                size={12}
                className={['rotate-180', current ? 'text-learning' : 'text-microcopy'].join(' ')}
              />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * ONE DAILY ITEM. Three states, and they are genuinely different rows: finished (struck through,
 * with the day it was finished), the next one up (heavier, white ground, the `today` chip, and a
 * REAL checkbox), and not yet (quiet, with the day the plan wants it).
 *
 * The checkbox writes through the data contract, so ticking it here moves the pill at the top of
 * the screen, the unit's own count, the pace line and the session log at once.
 */
function ItemRow({
  item,
  today,
  isNext,
}: {
  item: LearningUnitItem
  today: ISODate
  /** Exactly one row in the whole outline gets this: the next unfinished item in plan order. */
  isNext: boolean
}) {
  const toggle = useToggleLearningItem()

  if (item.done) {
    return (
      <label className="flex cursor-pointer items-center gap-[11px] px-[14px] py-2 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-surface-2">
        <input
          type="checkbox"
          checked
          onChange={() => toggle.mutate({ id: item.id, done: false })}
          className="peer sr-only"
        />
        <Tick size={13} className="flex-none text-live-dot" />
        <span className="min-w-0 flex-1 truncate text-[12px] font-normal leading-[1.3] text-muted line-through">
          {item.title}
        </span>
        {item.resource && <RowResourceLink item={item} />}
        <span className="flex-none text-[10px] font-normal leading-none text-faintest-2">
          {item.completedDate ? formatShortDate(item.completedDate) : ''}
        </span>
      </label>
    )
  }

  return (
    <label
      className={[
        'flex cursor-pointer items-center gap-[11px] px-[14px] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
        isNext
          ? 'border-y border-teal-fill bg-surface py-[10px]'
          : 'py-2 hover:bg-surface-2',
      ].join(' ')}
    >
      <input
        type="checkbox"
        checked={false}
        onChange={() => toggle.mutate({ id: item.id, done: true })}
        className="peer sr-only"
      />
      <span
        className={[
          'flex-none',
          isNext
            ? 'size-[14px] rounded-keycap border-[1.8px] border-learning'
            : 'size-[13px] rounded-[4px] border-[1.6px] border-line-2',
        ].join(' ')}
        aria-hidden
      />
      <span
        className={[
          'min-w-0 flex-1 truncate leading-[1.3]',
          isNext ? 'text-[12.5px] font-semibold' : 'text-[12px] font-normal text-ink-3',
        ].join(' ')}
      >
        {item.title}
      </span>
      {item.resource && <RowResourceLink item={item} strong={isNext} />}
      {isNext ? (
        <span className="flex-none rounded-pill bg-teal-fill px-2 py-[2px] text-[10px] font-semibold leading-[1.6] text-teal">
          today
        </span>
      ) : (
        <span className="flex-none text-[10px] font-normal leading-none text-faintest-2">
          {relativeDay(item.plannedDate, today)}
        </span>
      )}
    </label>
  )
}

/**
 * THE ITEM'S OWN LINK, so any row in the outline opens its page without going through Today. A link
 * inside the row's <label> is interactive content, so clicking it opens the page and does NOT tick
 * the checkbox. Same new-tab contract as the Today board's button: the urls come from an imported
 * plan, so the opened tab gets no handle back on this one.
 */
function RowResourceLink({ item, strong = false }: { item: LearningUnitItem; strong?: boolean }) {
  const resource = item.resource
  if (!resource) return null
  return (
    <a
      href={resource.url}
      target="_blank"
      rel="noreferrer noopener"
      title={`Open ${resource.label} in a new tab`}
      className={[
        'flex flex-none items-center gap-[5px] rounded-keycap border px-[7px] py-[3px] text-[10px] font-medium leading-none transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)]',
        strong
          ? 'border-teal-200 text-teal hover:bg-teal-fill'
          : 'border-line text-muted hover:text-ink',
      ].join(' ')}
    >
      {resource.kind}
      <ExternalLink size={11} className="flex-none" />
      <span className="sr-only">, opens {resource.label} in a new tab</span>
    </a>
  )
}
